import { ChangeDetectorRef, Component, inject, Injector, Input, SimpleChanges, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControlDirective,
  NgControl,
  UntypedFormControl,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { RulesEngineInputService } from './rules-engine-input.service';
@Component({
  template: '',
})
export abstract class ControlValueAccessorConnector
  implements ControlValueAccessor
{
  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;

  @Input()
  formControl: UntypedFormControl;

  @Input()
  formControlName: string;

  @Input()
  loading: boolean;

  /**
   * Instruct the UI to trigger the rules engine when this input changes.
   */
  @Input()
  rulesEngineInput: boolean;

  _readonly: boolean;

  _control: AbstractControl;

  onChangeFn: Function;

  onTouchFn: Function;

  destroy$ = new Subject<boolean>();

  private injector = inject(Injector);

  protected cdr = inject(ChangeDetectorRef);

  protected _rulesEngineInputService = inject(RulesEngineInputService, { optional: true });

  private _shouldOverrideReadonly: boolean;

  ngOnChanges(changes: SimpleChanges): void {
    if (this._shouldOverrideReadonly === undefined) {
      this._shouldOverrideReadonly = !changes.readonly?.firstChange;
    }
  }

  get control() {
    if (!this._control) { 
      this._control = this.ngControl.control ||
        this.formControl ||
        this.controlContainer.control.get(this.formControlName)
    }

    return this._control;
  }

  ngAfterViewInit(): void {
    if (this.rulesEngineInput) {
      this._rulesEngineInputService?.registerControl(this.control, this.formControlName);
    }
  }

  get ngControl() {
    return this.injector.get(NgControl);
  }

  get controlContainer() {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: any): void {
    this.onTouchFn = fn;
    this.formControlDirective.valueAccessor.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.formControlDirective.valueAccessor.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective.valueAccessor.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective.valueAccessor.setDisabledState(isDisabled);
    if (this._shouldOverrideReadonly) {
      this._readonly = isDisabled;
    }
  }

  @Input()
  get readonly(): boolean {
    if (!this.control) {
      return this._readonly;
    }
    return this._readonly ?? this.control['readOnly'];
  }

  set readonly(readonly: boolean) {
    this._readonly = readonly;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
